import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Layers, Check, ChevronDown, ChevronRight, Sparkles, Image as ImageIcon, RefreshCw } from "lucide-react";
import { sectorIcon, SECTOR_ICON_NAMES, SECTOR_MODULE_CATALOG, SECTOR_GROUP_ORDER, groupSectors, sectorGroupIcon } from "@/lib/sectors";
import { useAuth } from "@/context/AuthContext";

const LANDING_OPTS = [{ key: "/", label: "Panel (Genel)" }].concat(
  SECTOR_MODULE_CATALOG.flatMap((g) => g.items)
);
const ALL_MODULE_KEYS = SECTOR_MODULE_CATALOG.flatMap((g) => g.items.map((m) => m.key));
const emptyForm = { code: null, name: "", icon: "Building2", color: "#059669", landing: "/", group: "Diğer", modules: [], is_active: true };

export default function SectorsSettings() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const [togglingCode, setTogglingCode] = useState(null);
  const [provOpen, setProvOpen] = useState(false);
  const [provTarget, setProvTarget] = useState(null);
  const [provWithProducts, setProvWithProducts] = useState(true);
  const [provForce, setProvForce] = useState(false);
  const [provBusy, setProvBusy] = useState(false);
  const [provResult, setProvResult] = useState(null);
  const [provWithImages, setProvWithImages] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkWithProducts, setBulkWithProducts] = useState(true);
  const [bulkWithImages, setBulkWithImages] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [imgBusyCode, setImgBusyCode] = useState(null);
  const [provSource, setProvSource] = useState("template");
  const [templateCodes, setTemplateCodes] = useState([]);
  const [provStatus, setProvStatus] = useState({});
  const [provLoaded, setProvLoaded] = useState(false);
  const { user, activeSector, refreshSectors } = useAuth();
  const isOwner = !!user?.is_owner;
  const isAdmin = user?.role === "admin";

  const openProvision = (s) => {
    setProvTarget(s);
    setProvWithProducts(true);
    const alreadyProv = !!(provStatus[s.code] && provStatus[s.code].provisioned);
    setProvForce(alreadyProv);
    setProvWithImages(false);
    setProvResult(null);
    setProvSource(templateCodes.includes(s.code) ? "template" : "ai");
    setProvOpen(true);
  };
  const generateSectorImages = async (s) => {
    setImgBusyCode(s.code);
    try {
      const { data } = await api.post(`/sectors/${s.code}/generate-images`, {});
      if (data.started > 0) toast.success(data.message || `${data.started} ürün için görsel üretimi başladı`);
      else toast.info(data.message || "Bu sektörde görselsiz ürün kalmadı");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setImgBusyCode(null); }
  };
  const runShowcase = async () => {
    setImgBusyCode("__all__");
    try {
      const { data } = await api.post("/sectors/showcase-images", {});
      if (data.started > 0) toast.success(data.message || `${data.started} ürün için vitrin görselleri başladı`);
      else toast.info(data.message || "Görselsiz ürün kalmadı — vitrin tam dolu");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setImgBusyCode(null); }
  };
  const runProvision = async () => {
    if (!provTarget) return;
    setProvBusy(true);
    try {
      const { data } = await api.post(`/sectors/${provTarget.code}/provision`, null, { params: { with_products: provWithProducts, force: provForce, source: provSource, with_images: provWithImages } });
      setProvResult(data);
      if (data.skipped) toast.info("Bu sektör zaten kurulu");
      else toast.success(`${data.categories_added} kategori + ${data.products_added} ürün eklendi`);
      load();
      loadProvStatus();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setProvBusy(false); }
  };
  const loadProvStatus = () => api.get("/sectors/provision-status").then((r) => { setProvStatus(r.data.status || {}); setProvLoaded(true); }).catch(() => setProvLoaded(true));
  const provisionAll = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/sectors/provision-all-templates", { with_products: bulkWithProducts, with_images: bulkWithImages });
      setBulkResult(data);
      toast.success(`${data.provisioned.length} sektör kuruldu · ${data.products_added} ürün`);
      load();
      loadProvStatus();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/sectors"); setList(r.data || []); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/sectors/provision-templates").then((r) => setTemplateCodes(r.data.codes || [])).catch(() => {}); loadProvStatus(); }, []);

  const openNew = () => { setEditing(false); setForm(emptyForm); setOpen(true); };
  const openEdit = (s) => { setEditing(true); setForm({ code: s.code, name: s.name, icon: s.icon || "Building2", color: s.color || "#059669", landing: s.landing || "/", group: s.group || "Diğer", modules: s.modules || [], is_active: s.is_active !== false }); setOpen(true); };

  const toggleModule = (route) => setForm((f) => ({ ...f, modules: f.modules.includes(route) ? f.modules.filter((x) => x !== route) : [...f.modules, route] }));
  const selectAllModules = () => setForm((f) => ({ ...f, modules: f.modules.length === ALL_MODULE_KEYS.length ? [] : [...ALL_MODULE_KEYS] }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Sektör adı zorunludur");
    setSaving(true);
    try {
      const body = { name: form.name.trim(), icon: form.icon, color: form.color, landing: form.landing, group: form.group, modules: form.modules, is_active: form.is_active };
      if (editing) await api.put(`/sectors/${form.code}`, body);
      else await api.post("/sectors", { ...body, code: form.name });
      toast.success(editing ? "Sektör güncellendi" : "Sektör eklendi");
      setOpen(false); load(); refreshSectors?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (s) => {
    if (!window.confirm(`"${s.name}" sektörü silinsin mi? Bu sektöre atanmış kullanıcılardan da kaldırılır.`)) return;
    try { await api.delete(`/sectors/${s.code}`); toast.success("Sektör silindi"); load(); refreshSectors?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (s) => {
    const cur = s.is_active !== false;
    const activeNow = list.filter((x) => x.is_active !== false).length;
    if (cur && activeNow <= 1) { toast.error("En az bir sektör açık kalmalı — son açık sektör kapatılamaz."); return; }
    setTogglingCode(s.code);
    const next = !cur;
    setList((ls) => ls.map((x) => (x.code === s.code ? { ...x, is_active: next } : x)));
    try {
      await api.put(`/sectors/${s.code}`, { is_active: next });
      toast.success(next ? `"${s.name}" açıldı (girişte görünür)` : `"${s.name}" kapatıldı — modül/ayar bilgileri KORUNUR, tekrar açınca kaldığı yerden gelir`);
      await refreshSectors?.();
    }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setList((ls) => ls.map((x) => (x.code === s.code ? { ...x, is_active: !next } : x))); }
    finally { setTogglingCode(null); }
  };

  // Big BOSS: ★ Tümü dışındaki her sektörü kapat (test için) — bilgiler korunur, tek tıkla geri açılır.
  const closeAllButFull = async () => {
    if (!window.confirm("★ Tümü dışındaki TÜM sektörler KAPATILACAK (bilgileri korunur, sonra tek tıkla açılır). Yalnız test için idealdir. Devam?")) return;
    setTogglingCode("__bulk__");
    try {
      const reqs = list.map((s) => {
        const shouldBeActive = s.code === "full";
        if ((s.is_active !== false) === shouldBeActive) return null;
        return api.put(`/sectors/${s.code}`, { is_active: shouldBeActive });
      }).filter(Boolean);
      await Promise.all(reqs);
      toast.success("Yalnızca ★ Tümü açık bırakıldı");
      await load(); await refreshSectors?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setTogglingCode(null); }
  };

  const activeCount = list.filter((x) => x.is_active !== false).length;
  const groups = groupSectors(list);
  const activeGroupName = groups.find((g) => g.items.some((s) => s.code === activeSector))?.group;
  const isOpen = (g, i) => (g in openGroups ? openGroups[g] : (i === 0 || g === activeGroupName));
  const allOpen = groups.length > 0 && groups.every((g, i) => isOpen(g.group, i));
  const setAll = (v) => setOpenGroups(Object.fromEntries(groups.map((g) => [g.group, v])));
  const FLAT = list.length <= 8;

  const renderCard = (s) => {
    const Icon = sectorIcon(s.icon);
    const on = s.is_active !== false;
    const isCurrent = s.code === activeSector;
    const lockOff = on && activeCount <= 1;
    return (
      <div key={s.code} data-testid={`sector-row-${s.code}`} className={`rounded-xl border p-3 transition-colors ${isCurrent ? "border-brand-green ring-1 ring-brand-green/30 bg-brand-green/[0.03]" : on ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50/70 opacity-70"}`}>
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white shadow-sm" style={{ backgroundColor: s.color || "#059669" }}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="min-w-0 break-words text-sm font-bold leading-tight text-slate-800">{s.name}{isCurrent && <span data-testid={`sector-current-${s.code}`} className="ml-1 inline-block rounded-full bg-brand-green px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">şu an</span>}</p>
              <Switch data-testid={`sector-active-toggle-${s.code}`} className="mt-0.5 shrink-0" checked={on} disabled={togglingCode === s.code || togglingCode === "__bulk__" || lockOff} onCheckedChange={() => toggleActive(s)} title={lockOff ? "Son açık sektör — kapatılamaz" : (on ? "Açık (girişte görünür) — kapatmak için tıkla · bilgiler korunur" : "Kapalı — açmak için tıkla")} />
            </div>
            <div className="mt-1 flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-1.5">
                <p className="min-w-0 truncate text-[11px] text-slate-400">{(s.modules || []).length} modül · {s.is_builtin ? "yerleşik" : "özel"}</p>
                {provLoaded && isAdmin && s.code !== "full" && !(provStatus[s.code] && provStatus[s.code].provisioned) && (
                  <button data-testid={`sector-setup-badge-${s.code}`} onClick={() => openProvision(s)} className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700 ring-1 ring-inset ring-amber-200 hover:bg-amber-200" title="Bu sektöre henüz kategori/ürün kurulmadı — tıkla, kurulum sihirbazını aç">
                    <Sparkles className="h-3 w-3" /> Kurulum bekliyor
                  </button>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                {isAdmin && s.code !== "full" && provStatus[s.code]?.provisioned && <button data-testid={`sector-genimg-${s.code}`} onClick={() => generateSectorImages(s)} disabled={imgBusyCode === s.code} className="rounded-md p-1.5 text-slate-400 hover:bg-sky-50 hover:text-sky-500 disabled:opacity-50" title="Bu sektörün ürünlerine AI ile görsel üret (arka planda · günlük limite sadık)">{imgBusyCode === s.code ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}</button>}
                {isAdmin && s.code !== "full" && <button data-testid={`sector-provision-${s.code}`} onClick={() => openProvision(s)} className="rounded-md p-1.5 text-slate-400 hover:bg-amber-50 hover:text-amber-500" title={provStatus[s.code]?.provisioned ? "Şablonu tazele / yeniden kur — mevcut silinmez, eksik ürünler eklenir" : "Kategori + örnek ürün kur (yalnız bu sektöre)"}><Sparkles className="h-4 w-4" /></button>}
                <button data-testid={`sector-edit-${s.code}`} onClick={() => openEdit(s)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-brand-green"><Pencil className="h-4 w-4" /></button>
                {isOwner && s.code !== "full" && <button data-testid={`sector-del-${s.code}`} onClick={() => del(s)} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="sectors-settings" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><Layers className="h-5 w-5 text-brand-green" /> Sektörler <span className="rounded-full bg-brand-green/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand-green">tek merkez</span></p>
          <p className="mt-1 text-sm text-slate-500">{isOwner
            ? "Sektörleri BURADAN yönetin: grupları açın/kapatın, anahtarla hızlı aç-kapa yapın. Kapatınca modül/ayar bilgileri KORUNUR — tekrar açınca kaldığı yerden gelir. ★ Tümü tüm modülleri kapsar."
            : "Hesabınıza tanımlı sektörler. Adını/simgesini düzenleyebilirsiniz; yeni sektör ekleme veya silme yalnızca sistem sahibinde (Big BOSS)."}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isOwner && <Button data-testid="sectors-only-full" variant="outline" size="sm" onClick={closeAllButFull} disabled={togglingCode === "__bulk__"} className="shrink-0 gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"><Sparkles className="h-4 w-4" /> Yalnız ★ Tümü</Button>}
          <Button data-testid="sectors-expand-all" variant="outline" size="sm" onClick={() => setAll(!allOpen)} className="shrink-0">{allOpen ? "Tümünü Kapat" : "Tümünü Aç"}</Button>
          {isAdmin && <Button data-testid="sectors-provision-all-btn" variant="outline" size="sm" onClick={() => { setBulkResult(null); setBulkWithProducts(true); setBulkWithImages(false); setBulkOpen(true); }} className="shrink-0 gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"><Sparkles className="h-4 w-4" /> Tümünü Hazır Şablonla Kur</Button>}
          {isAdmin && <Button data-testid="sectors-showcase-images-btn" variant="outline" size="sm" onClick={runShowcase} disabled={imgBusyCode === "__all__"} className="shrink-0 gap-1.5 border-sky-300 text-sky-700 hover:bg-sky-50" title="Kurulu sektörlerin görselsiz ürünlerine AI görsel üret — standart ürünler öncelikli, arka planda, günlük limite sadık (kredi harcar)">{imgBusyCode === "__all__" ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />} Tüm Sektör Vitrini (AI Görsel)</Button>}
          {isOwner && <Button data-testid="sector-add-btn" onClick={openNew} className="shrink-0 gap-1.5"><Plus className="h-4 w-4" /> Yeni Sektör</Button>}
        </div>
      </div>

      {/* Nerede ne yapılır — dağınıklığı gidermek için tek bakışta rehber */}
      <div data-testid="sectors-where-guide" className="grid gap-2 rounded-xl border border-slate-200 bg-slate-50/70 p-3 text-[11px] leading-snug text-slate-500 sm:grid-cols-3">
        <p><b className="text-slate-700">① Burası (Sektörler):</b> sektörü oluştur/düzenle, aç-kapa, modüllerini belirle. Tek yönetim merkezi.</p>
        <p><b className="text-slate-700">② Üst bar “Sektör”:</b> yalnızca o an hangi sektörün menüsünü GÖRDÜĞÜNÜ seçer — ayar değil, görünüm.</p>
        <p><b className="text-slate-700">③ Platform → Kiracı:</b> bir müşteriye hangi sektörlerin tanımlı olduğunu Big BOSS atar.</p>
      </div>

      {loading ? <p className="text-sm text-slate-400">Yükleniyor…</p> : FLAT ? (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="sector-flat-list">
          {list.map(renderCard)}
        </div>
      ) : (
        <div className="space-y-2.5">
          {groups.map((grp, gi) => {
            const opened = isOpen(grp.group, gi);
            const gActive = grp.items.filter((s) => s.is_active !== false).length;
            const GroupIcon = sectorGroupIcon(grp.group);
            const isStar = grp.group === "⭐ Özel";
            return (
              <div key={grp.group} data-testid={`sector-group-${grp.group}`} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <button type="button" data-testid={`sector-group-toggle-${grp.group}`} onClick={() => setOpenGroups((o) => ({ ...o, [grp.group]: !opened }))}
                  className="flex w-full items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-slate-50">
                  <span className="shrink-0 text-slate-400">{opened ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</span>
                  <GroupIcon className={`h-5 w-5 shrink-0 ${isStar ? "text-amber-500" : "text-brand-green"}`} />
                  <span className="flex-1 font-heading text-sm font-bold text-slate-800">{grp.group}</span>
                  <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">{gActive}/{grp.items.length} açık</span>
                </button>
                {opened && (
                  <div className="grid gap-2 border-t border-slate-100 p-3 sm:grid-cols-2 lg:grid-cols-3">
                    {grp.items.map(renderCard)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="sector-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Sektörü Düzenle" : "Yeni Sektör"}</DialogTitle>
            <DialogDescription>Sektör adı, grubu, simgesi, rengi ve bu sektörde görünecek menü modülleri.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><Label>Sektör Adı *</Label><Input data-testid="sector-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="Örn: Tarım" /></div>
              <div>
                <Label>Grup</Label>
                <select data-testid="sector-group" value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })}
                  className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                  {SECTOR_GROUP_ORDER.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label>Renk</Label>
                <div className="mt-1 flex items-center gap-2">
                  <input type="color" data-testid="sector-color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-9 w-12 shrink-0 cursor-pointer rounded border border-slate-300 bg-white p-0.5" />
                  <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="font-mono" />
                </div>
              </div>
              <label className="flex items-end gap-2 pb-1">
                <input type="checkbox" data-testid="sector-active" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="h-4 w-4 rounded border-slate-300" />
                <span className="text-sm font-medium text-slate-700">Aktif (giriş ekranında görünür)</span>
              </label>
            </div>

            <div>
              <Label>Simge</Label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {SECTOR_ICON_NAMES.map((n) => {
                  const Icon = sectorIcon(n);
                  const active = form.icon === n;
                  return (
                    <button key={n} type="button" data-testid={`sector-icon-${n}`} onClick={() => setForm({ ...form, icon: n })}
                      className={`flex h-9 w-9 items-center justify-center rounded-md border transition-colors ${active ? "border-brand-green bg-brand-green/10 text-brand-green" : "border-slate-200 text-slate-400 hover:border-slate-300"}`}>
                      <Icon className="h-4 w-4" />
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label>Açılış Ekranı</Label>
              <select data-testid="sector-landing" value={form.landing} onChange={(e) => setForm({ ...form, landing: e.target.value })}
                className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                {LANDING_OPTS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>Görünecek Modüller ({form.modules.length})</Label>
                <button type="button" data-testid="sector-modules-all" onClick={selectAllModules} className="text-xs font-semibold text-brand-green hover:underline">{form.modules.length === ALL_MODULE_KEYS.length ? "Tümünü kaldır" : "Tümünü seç"}</button>
              </div>
              <p className="mb-2 mt-0.5 text-[11px] text-slate-400">Seçili modüller bu sektörün menüsünde görünür. (Panel, Mesajlar, AI ve tüm Yönetim menüsü her sektörde görünür.)</p>
              <div className="space-y-3 rounded-lg border border-slate-100 bg-slate-50/60 p-3">
                {SECTOR_MODULE_CATALOG.map((g) => (
                  <div key={g.group}>
                    <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">{g.group}</p>
                    <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                      {g.items.map((m) => {
                        const on = form.modules.includes(m.key);
                        return (
                          <button key={m.key} type="button" data-testid={`sector-module-${m.key.slice(1)}`} onClick={() => toggleModule(m.key)}
                            className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-left text-sm transition-colors ${on ? "border-brand-green bg-brand-green/5 text-slate-800" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}>
                            <span className={`flex h-4 w-4 items-center justify-center rounded border ${on ? "border-brand-green bg-brand-green text-white" : "border-slate-300"}`}>{on && <Check className="h-3 w-3" />}</span>
                            {m.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
              <Button data-testid="sector-save" onClick={save} disabled={saving} className="gap-1.5">{saving ? "Kaydediliyor…" : (editing ? "Güncelle" : "Ekle")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sektör Kurulum Sihirbazı — AI ile kategori + örnek ürün (yalnız bu sektöre etiketli, sızmaz) */}
      <Dialog open={provOpen} onOpenChange={setProvOpen}>
        <DialogContent data-testid="sector-provision-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><Sparkles className="h-5 w-5 text-amber-500" /> Sektör Kurulum Sihirbazı — {provTarget?.name}</DialogTitle>
            <DialogDescription>Bu sektöre ÖZEL mantıklı bir kategori ağacı ve örnek ürünler AI ile oluşturulur. Mevcut hiçbir veri silinmez; üretilenler yalnız "{provTarget?.name}" sektörüne etiketlenir (başka sektöre sızmaz).</DialogDescription>
          </DialogHeader>
          {!provResult ? (
            <div className="space-y-3">
              <div>
                <p className="mb-1.5 text-xs font-semibold text-slate-600">Kaynak</p>
                <div className="grid gap-2 sm:grid-cols-2" data-testid="sector-provision-source">
                  <button type="button" data-testid="sector-provision-source-template" disabled={!templateCodes.includes(provTarget?.code)}
                    onClick={() => setProvSource("template")}
                    className={`flex flex-col items-start rounded-lg px-3 py-2 text-left ring-1 ring-inset transition-colors ${provSource === "template" ? "bg-emerald-50 ring-brand-green" : "bg-white ring-slate-200 hover:bg-slate-50"} ${!templateCodes.includes(provTarget?.code) ? "cursor-not-allowed opacity-50" : ""}`}>
                    <span className={`text-sm font-bold ${provSource === "template" ? "text-brand-green" : "text-slate-700"}`}>⚡ Hazır Şablon (anında)</span>
                    <span className="text-[11px] text-slate-400">{templateCodes.includes(provTarget?.code) ? "AI beklemeden anında kurar · kredi harcamaz" : "Bu sektör için hazır şablon yok"}</span>
                  </button>
                  <button type="button" data-testid="sector-provision-source-ai" onClick={() => setProvSource("ai")}
                    className={`flex flex-col items-start rounded-lg px-3 py-2 text-left ring-1 ring-inset transition-colors ${provSource === "ai" ? "bg-emerald-50 ring-brand-green" : "bg-white ring-slate-200 hover:bg-slate-50"}`}>
                    <span className={`text-sm font-bold ${provSource === "ai" ? "text-brand-green" : "text-slate-700"}`}>🤖 AI ile Üret</span>
                    <span className="text-[11px] text-slate-400">Sektöre özel mantıklı üretim · AI kredisi harcar</span>
                  </button>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" data-testid="sector-provision-with-products" checked={provWithProducts} onChange={(e) => setProvWithProducts(e.target.checked)} className="h-4 w-4 rounded border-slate-300" /> Örnek ürünler de üret (kategorilerle birlikte)</label>
              <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" data-testid="sector-provision-force" checked={provForce} onChange={(e) => setProvForce(e.target.checked)} className="h-4 w-4 rounded border-slate-300" /> Şablonu yeniden çalıştır / tazele <span className="text-[11px] text-slate-400">(mevcut silinmez; eksik/yeni ürünler eklenir)</span></label>
              <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" data-testid="sector-provision-with-images" checked={provWithImages} onChange={(e) => setProvWithImages(e.target.checked)} disabled={!provWithProducts} className="h-4 w-4 rounded border-slate-300" /> Ürün görsellerini de AI ile üret <span className="text-[11px] text-amber-600">(arka planda · kredi harcar)</span></label>
              <div className="rounded-lg bg-amber-50 p-2.5 text-[11px] text-amber-700 ring-1 ring-inset ring-amber-200">{provSource === "ai" ? "AI kredisi harcar. Kurulum 10–40 sn sürebilir; lütfen bekleyin." : "Hazır şablon anında kurulur — AI/kredi harcamaz."}</div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setProvOpen(false)}>Vazgeç</Button>
                <Button data-testid="sector-provision-run" onClick={runProvision} disabled={provBusy} className="gap-1.5 bg-amber-500 text-white hover:bg-amber-600">{provBusy ? "Kuruluyor…" : (provSource === "template" ? "⚡ Hazır Şablonla Kur" : "🤖 AI ile Kur")}</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="sector-provision-result">
              {provResult.skipped ? (
                <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">Bu sektör zaten kurulu (<b>{provResult.categories}</b> kategori, <b>{provResult.products}</b> ürün). Üzerine eklemek için "Zaten kuruluysa üzerine yeniden ekle" seçeneğini işaretleyip tekrar deneyin.</p>
              ) : (
                <p className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700">✓ Kurulum tamam — <b>{provResult.categories_added}</b> kategori + <b>{provResult.products_added}</b> ürün eklendi (yalnız "{provTarget?.name}" sektörüne).{provResult.images_started > 0 && <> <b>{provResult.images_started}</b> ürün görseli arka planda üretiliyor.</>}</p>
              )}
              <div className="flex justify-end"><Button data-testid="sector-provision-close" onClick={() => { setProvOpen(false); setProvResult(null); }} className="gap-1.5">Kapat</Button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Toplu kurulum — şablonu olan ve kurulmamış tüm sektörleri tek tıkla (anında) kur */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="sectors-bulk-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><Sparkles className="h-5 w-5 text-amber-500" /> Tüm Kurulmamış Sektörleri Kur</DialogTitle>
            <DialogDescription>Şablonu olan ve HENÜZ kurulmamış tüm sektörlere hazır kategori + örnek ürünler ANINDA (AI'sız, kredi harcamadan) kurulur. Mevcut hiçbir veri silinmez; her ürün yalnız kendi sektörüne etiketlenir.</DialogDescription>
          </DialogHeader>
          {!bulkResult ? (
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" data-testid="sectors-bulk-with-products" checked={bulkWithProducts} onChange={(e) => setBulkWithProducts(e.target.checked)} className="h-4 w-4 rounded border-slate-300" /> Örnek ürünleri de kur</label>
              <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" data-testid="sectors-bulk-with-images" checked={bulkWithImages} onChange={(e) => setBulkWithImages(e.target.checked)} disabled={!bulkWithProducts} className="h-4 w-4 rounded border-slate-300" /> Ürün görsellerini de AI ile üret <span className="text-[11px] text-amber-600">(arka planda · kredi harcar)</span></label>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setBulkOpen(false)}>Vazgeç</Button>
                <Button data-testid="sectors-bulk-run" onClick={provisionAll} disabled={bulkBusy} className="gap-1.5 bg-amber-500 text-white hover:bg-amber-600">{bulkBusy ? "Kuruluyor…" : "⚡ Tümünü Kur"}</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="sectors-bulk-result">
              <p className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700">✓ <b>{bulkResult.provisioned.length}</b> sektör kuruldu — <b>{bulkResult.categories_added}</b> kategori + <b>{bulkResult.products_added}</b> ürün.{bulkResult.images_started > 0 && <> <b>{bulkResult.images_started}</b> görsel arka planda üretiliyor.</>} {bulkResult.skipped.length > 0 && <span className="text-slate-500">({bulkResult.skipped.length} sektör zaten kuruluydu, atlandı.)</span>}</p>
              <div className="flex justify-end"><Button data-testid="sectors-bulk-close" onClick={() => { setBulkOpen(false); setBulkResult(null); }}>Kapat</Button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
